import React, { useEffect, useRef, useState } from 'react';
import { ProjectType } from '../../projectsData';
import './projectDetails.css';

// Function to format markdown-like content to HTML
const formatReadme = (content: string): string => {
  if (!content) return '';
  
  // Replace headers
  let html = content
    .replace(/^# (.*$)/gm, '<h1>$1</h1>')
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    
    // Replace emphasis
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    
    // Replace lists
    .replace(/^\* (.*$)/gm, '<ul><li>$1</li></ul>')
    .replace(/<\/ul>\s*<ul>/g, '')
    
    // Replace links
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    
    // Replace code blocks
    .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    
    // Replace line breaks
    .replace(/\n/g, '<br />');

  return html;
};

interface NL2SQLProjectDetailsProps {
  project: ProjectType;
  onClose: () => void;
}

const SECTIONS = [
  { id: 'description', label: 'Description' },
  { id: 'features',    label: 'Features'    },
  { id: 'tech',        label: 'Stack'       },
  { id: 'links',       label: 'Links'       },
];

const NL2SQLProjectDetails: React.FC<NL2SQLProjectDetailsProps> = ({ project, onClose }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const heroRef      = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      setProgress(scrollHeight > clientHeight ? (scrollTop / (scrollHeight - clientHeight)) * 100 : 0);
      if (heroRef.current) heroRef.current.style.transform = `translateY(${scrollTop * 0.45}px)`;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const sectionEls = SECTIONS.map(s => el.querySelector(`[data-section="${s.id}"]`)).filter(Boolean) as Element[];
    const obs = new IntersectionObserver(
      entries => entries.forEach(entry => {
        if (entry.isIntersecting) {
          const idx = sectionEls.indexOf(entry.target);
          if (idx !== -1) setActiveSection(idx);
        }
      }),
      { threshold: 0.35, root: el }
    );
    sectionEls.forEach(s => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    containerRef.current?.querySelector(`[data-section="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Prevent body scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    // Add escape key listener to close modal
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    
    window.addEventListener('keydown', handleEsc);
    
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  return (
    <div className="project-details__container" ref={containerRef}>
      <div className="project-details__header">
        <div className="project-details__header-content">
          <h1>
            <span className="project-details__header-icon">
              <i className="uil uil-code-branch"></i>
            </span>
            {project.title}
          </h1>
          <button 
            className="project-details__close" 
            onClick={onClose}
            aria-label="Close project details"
          >
            <i className="uil uil-times"></i>
          </button>
        </div>
        <div className="pdp__progress" style={{ width: `${progress}%` }} />
      </div>
      
      <nav className="pdp__sections-nav" aria-label="Page sections">
        {SECTIONS.map((s, i) => (
          <button
            key={s.id}
            className={`pdp__section-dot${i === activeSection ? ' pdp__section-dot--active' : ''}`}
            onClick={() => scrollTo(s.id)}
            aria-label={s.label}
          >
            <span className="pdp__section-dot-label">{s.label}</span>
          </button>
        ))}
      </nav>

      <div className="project-details__body">
        <div className="project-details__main-image">
          <div className="pdp__parallax-wrap" ref={heroRef}>
          <object
            type="image/svg+xml"
            data="/assets/images/nl2sql-architecture.svg"
            width="100%"
            height="auto"
            title="NL2SQL Architecture Diagram"
            aria-label="Architecture diagram showing T5 and Gemma models"
            style={{ backgroundColor: '#f8f9fa', borderRadius: '12px' }}
          >
            <img
              src="/assets/png/nl2sql.svg"
              alt={project.title}
              style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
            />
          </object>
          </div>{/* end pdp__parallax-wrap */}
          <div className="project-details__category-badge">
            {project.category}
          </div>
        </div>
        
        <div className="project-details__content">
          <div className="project-details__section" data-section="description">
            <h2>
              <i className="uil uil-document-info"></i>
              Description
            </h2>
            <p>{project.description}</p>
          </div>

          <div className="project-details__section" data-section="features">
            <h2>
              <i className="uil uil-star"></i>
              Key Features
            </h2>
            <ul className="project-details__features">
              {project.features.map((feature, index) => (
                <li key={index}>
                  <i className="uil uil-check-circle"></i>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="project-details__section">
            <h2>
              <i className="uil uil-window"></i>
              Approaches Comparison
            </h2>
            <div className="project-details__features">
              <li>
                <i className="uil uil-robot"></i>
                <span><strong>T5 Fine-tuning:</strong> Trained on specific text-to-SQL dataset, achieving SQL exact match of 45-50% and record F1 score of 75-80%.</span>
              </li>
              <li>
                <i className="uil uil-brain"></i>
                <span><strong>In-context Learning:</strong> Zero-shot performance of 35-40% SQL match, rising to 40-45% with few-shot learning and prompt engineering.</span>
              </li>
            </div>
          </div>
          
          <div className="project-details__section" data-section="tech">
            <h2>
              <i className="uil uil-window-grid"></i>
              Technologies
            </h2>
            <div className="project-details__tech">
              {project.tech.map((item, index) => (
                <span key={index} className="project-details__tech-item">
                  {item}
                </span>
              ))}
            </div>
          </div>

          {project.readmeContent && (
            <div className="project-details__section">
              <h2>
                <i className="uil uil-file-alt"></i>
                Project Documentation
              </h2>
              <div className="project-details__readme">
                <div className="markdown-content" dangerouslySetInnerHTML={{ __html: formatReadme(project.readmeContent) }} />
              </div>
            </div>
          )}
          
          <div className="project-details__section" data-section="links">
            <h2>
              <i className="uil uil-link"></i>
              Links
            </h2>
            <div className="project-details__links">
              <a
                href={project.github} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="project-details__link"
              >
                <i className="uil uil-github"></i> View on GitHub
              </a>
              
              {project.demo && (
                <a 
                  href={project.demo} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="project-details__link project-details__link--demo"
                >
                  <i className="uil uil-eye"></i> Live Demo
                </a>
              )}
            </div>
          </div>
        </div>
        
        <div className="project-details__footer">
          <button 
            onClick={onClose}
            className="project-details__back-button"
          >
            <i className="uil uil-arrow-left"></i> Back to Projects
          </button>
        </div>
      </div>
    </div>
  );
};

export default NL2SQLProjectDetails; 